/* The Mapped tab: the ERP invoice as PINT-OM fields, the step between Raw and XML.
 *
 * Input:  docId. Reads GET /api/documents/:id/mapped and the standard field catalogue.
 * Output: the mapping issues, then header fields, invoice lines and the VAT breakdown as
 *         tables (label, business term id, value); the raw JSON stays collapsed below.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { Expander, Notice } from "../../../design/components";
import { useDocumentMapped } from "../../../api/queries";
import { useStandardFields } from "../../hub/mapping/useProfileVersion";
import { catalogueFor, mappedValues, mappedView, type MappedRow, type MappedView } from "./mappedView";
import styles from "./DocumentInspector.module.css";

const hasValue = (row: MappedRow) => row.values.some(Boolean);
const numbered = (prefix: string, count: number) => Array.from({ length: count }, (_, i) => `${prefix} ${i + 1}`);

function FieldTable({ caption, rows, columns }: { caption: string; rows: MappedRow[]; columns: string[] }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <section className={styles.mappedBlock} aria-label={caption}>
      <h3 className={styles.mappedCaption}>{caption}</h3>
      <table className={`tbl ${styles.mappedTable}`}>
        <thead>
          <tr>
            <th scope="col">{t("documents.inspector.mapped.field", "Field")}</th>
            <th scope="col">{t("documents.inspector.mapped.ibt", "IBT")}</th>
            {columns.map((column) => (
              <th scope="col" key={column}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className={styles.mappedField}>
                {row.label}
                {row.label !== row.key ? <span className={styles.mappedKey}>{row.key}</span> : null}
              </td>
              <td className={styles.mappedIbt}>{row.ibt ?? ""}</td>
              {columns.map((column, i) => {
                const value = row.values[i] ?? "";
                return (
                  <td key={column} className={value ? styles.mappedValue : styles.mappedEmpty}>
                    {value || "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function IssuesNotice({ issues }: { issues: MappedView["issues"] }) {
  const { t } = useTranslation();
  if (issues.length === 0) return <Notice kind="ok">{t("documents.inspector.mapped.noIssues", "No mapping issues.")}</Notice>;
  return (
    <Notice kind="fail">
      <div>
        {t("documents.inspector.mapped.issuesCount", "{{count}} mapping issue", { count: issues.length })}
      </div>
      <ul className={styles.mappedIssues}>
        {issues.map((issue, i) => (
          <li key={`${issue.field}-${i}`}>
            <span className="mono">{issue.field}</span>: {issue.reason}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

export function MappedPane({ docId }: { docId: string }) {
  const { t } = useTranslation();
  const mapped = useDocumentMapped(docId);
  const fields = useStandardFields(mapped.data ? catalogueFor(mappedValues(mapped.data)) : undefined);
  const [hideEmpty, setHideEmpty] = useState(false);
  const view = useMemo(() => mappedView(mapped.data, fields.data ?? []), [mapped.data, fields.data]);

  if (mapped.isError) {
    return <Notice kind="fail">{t("documents.inspector.mapped.loadError", "The mapping result could not be loaded.")}</Notice>;
  }
  if (!mapped.data) return <p className={styles.sub}>{t("documents.inspector.mapped.loading", "Loading…")}</p>;

  const all = [...view.header, ...view.lines, ...view.taxes];
  const emptyCount = all.filter((row) => !hasValue(row)).length;
  const keep = (rows: MappedRow[]) => (hideEmpty ? rows.filter(hasValue) : rows);

  return (
    <div className="stack">
      <IssuesNotice issues={view.issues} />
      {all.length === 0 ? (
        <Notice kind="info">
          {t("documents.inspector.mapped.noValues", "No mapped values were recorded for this document.")}
        </Notice>
      ) : (
        <>
          <div className={styles.paneHead}>
            <span className={styles.sub}>
              {t("documents.inspector.mapped.fieldsCount", "{{count}} fields", { count: all.length })}
            </span>
            {emptyCount > 0 ? (
              <label className={styles.mappedToggle}>
                <input type="checkbox" checked={hideEmpty} onChange={(event) => setHideEmpty(event.target.checked)} />
                {t("documents.inspector.mapped.hideEmptyFields", "Hide empty fields ({{count}})", {
                  count: emptyCount,
                })}
              </label>
            ) : null}
          </div>
          <div className={styles.pane}>
            <FieldTable
              caption={t("documents.inspector.mapped.invoice", "Invoice")}
              rows={keep(view.header)}
              columns={[t("documents.inspector.mapped.value", "Value")]}
            />
            <FieldTable
              caption={t("documents.inspector.mapped.invoiceLines", "Invoice lines")}
              rows={keep(view.lines)}
              columns={numbered(t("documents.inspector.mapped.line", "Line"), view.lineCount)}
            />
            <FieldTable
              caption={t("documents.inspector.mapped.vatBreakdown", "VAT breakdown")}
              rows={keep(view.taxes)}
              columns={numbered(t("documents.inspector.mapped.group", "Group"), view.taxCount)}
            />
          </div>
        </>
      )}
      <Expander
        head={t("documents.inspector.mapped.resultJson", "Mapping result JSON")}
        body={<pre className={`code ${styles.pane}`}>{JSON.stringify(mapped.data, null, 2)}</pre>}
      />
    </div>
  );
}
